import { forwardRef, ReactNode } from 'react';
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import { SystemColors } from 'melp-design/style';

interface Props {
  items: Array<{ label: ReactNode; value: string; disabled?: boolean }>;
  label?: string;
  direction?: 'row' | 'column';
  size?: 'md' | 'xl';
  name?: string;
  value?: string;
  onChange?: (value: string) => void;
  info?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
}

export const RadioInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      items,
      label,
      direction = 'column',
      size = 'md',
      name,
      value,
      onChange,
      info,
      error,
      required,
      disabled,
    },
    forwardedRef,
  ) => {
    return (
      <FormControl
        error={!!error}
        required={required}
        disabled={disabled}
        component="fieldset"
      >
        {label ? (
          <FormLabel
            component="legend"
            id={name}
            sx={{
              fontSize: 14,
              color: SystemColors.grey[45],
              '&.Mui-focused': { color: SystemColors.grey[45] },
            }}
          >
            {label}
          </FormLabel>
        ) : null}

        <RadioGroup
          aria-labelledby={name}
          name={name}
          value={value}
          onChange={({ target }) => onChange?.(target.value)}
          row={direction === 'row'}
          sx={{ ...(direction === 'row' ? { gap: 1.25 } : {}) }}
          ref={forwardedRef}
        >
          {items.map((item) => (
            <FormControlLabel
              key={item.value}
              control={
                <Radio
                  required={required}
                  sx={{ padding: 1.75, pl: 1.25 }}
                  disableRipple
                />
              }
              label={item.label}
              value={item.value}
              disabled={item.disabled}
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: size === 'md' ? 14 : size === 'xl' ? 30 : undefined,
                },
              }}
            />
          ))}
        </RadioGroup>

        {info && <FormHelperText error={false}>{info}</FormHelperText>}
        {error && <FormHelperText error>{error}</FormHelperText>}
      </FormControl>
    );
  },
);

RadioInput.displayName = 'RadioInput';
